import React from 'react';
import {
  useDrop as useReactDrop,
  useDrag as useReactDrag,
  useDragLayer as useReactDragLayer,
} from 'react-dnd';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { TouchBackend } from 'react-dnd-touch-backend';
import { isTouchDevice } from 'utils/helper';
import {
  getSharedTableCapacityInfo,
  SharedTablePool,
} from 'utils/sharedTableCapacity';

const USE_DROP = true;
const RECENT_DRAG_GESTURE_WINDOW_MS = 500;
const TOUCH_DRAG_START_DELAY_MS = 80;
const TOUCH_DRAG_SLOP_PX = 8;
// Keep near-vertical gestures scrollable, but let diagonal sidebar pulls drag.
const VERTICAL_SCROLL_ANGLE_RANGES = [
  { start: 70, end: 110 },
  { start: 250, end: 290 },
];

let lastDragGestureAt = 0;

// The angle ranges above protect the sidebar list, which really scrolls. The
// floor plan is scroll-locked, so a near-vertical table drag there has nothing
// to scroll and must not be classified as one. An element that opted out of
// scrolling via `touch-action: none` is exactly such a drag-only surface.
let gestureStartedOnDragOnlySurface = false;

const trackGestureSurface = (event: TouchEvent | MouseEvent) => {
  // A second finger landing mid-drag must not reclassify the running gesture.
  if ('touches' in event && event.touches.length > 1) return;

  const target = event.target;
  gestureStartedOnDragOnlySurface =
    target instanceof Element &&
    window.getComputedStyle(target).touchAction === 'none';
};

export const markDragGesture = () => {
  lastDragGestureAt = Date.now();
};

export const getDragAndDropEnvironment = () => {
  const touchDevice = typeof window !== 'undefined' && isTouchDevice();
  const hasMouseLikePointer =
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    (window.matchMedia('(any-pointer: fine)').matches ||
      window.matchMedia('(any-hover: hover)').matches);

  return {
    backend: touchDevice ? ('touch' as const) : ('html5' as const),
    touchDevice,
    mouseEventsEnabled: !touchDevice || hasMouseLikePointer,
    touchOptions: {
      delayTouchStart: TOUCH_DRAG_START_DELAY_MS,
      touchSlop: TOUCH_DRAG_SLOP_PX,
      scrollAngleRanges: VERTICAL_SCROLL_ANGLE_RANGES,
      // Android fires contextmenu on long-press, which would abort drags.
      ignoreContextMenu: true,
    },
  };
};

export const hasRecentDragGesture = (
  windowMs = RECENT_DRAG_GESTURE_WINDOW_MS,
) => Date.now() - lastDragGestureAt < windowMs;

type DropTime = {
  id: string;
  from: number;
  till: number;
  guests?: number;
  tables?: string[];
};
type DragReservationItem = {
  id: string;
  from: number;
  till: number;
  guests?: number;
  origin?: 'sidebar' | 'floor-plan';
  preview?: {
    title: string;
    subtitle: string;
    status?: string;
    backgroundColor?: string;
  };
};

const canDropReservationOnTimes = (
  item: DragReservationItem | null,
  times: DropTime[],
  sharedTable?: { max: number; name?: string; pool?: SharedTablePool | null } | null,
) => {
  if (!item) return false;

  if (sharedTable) {
    return getSharedTableCapacityInfo(times, {
      start: item.from,
      end: item.till,
      requestedGuests: item.guests,
      tableMax: sharedTable.max,
      tableName: sharedTable.name,
      id: item.id,
      pool: sharedTable.pool,
    }).available;
  }

  return !times.some(
    (time) =>
      time.id === item.id ||
      (time.from <= item.from && time.till >= item.till) ||
      (time.from >= item.from && time.till <= item.till) ||
      (time.from <= item.from && time.till >= item.from) ||
      (time.from <= item.till && time.till >= item.till),
  );
};

export function useDrop<IItem = any>(
  onDrop: (item: IItem) => void | Promise<void>,
  accept: string[],
  times: DropTime[],
  sharedTable?: { max: number; name?: string; pool?: SharedTablePool | null } | null,
  disabled = false,
) {
  if (!USE_DROP)
    return {
      isOver: null,
      canDrop: null,
      drop: () => {},
    };

  const [{ isOver, canDrop }, drop] = useReactDrop({
    accept,
    canDrop: () => !disabled,
    drop: disabled ? () => undefined : onDrop,
    collect: (monitor) => {
      if (disabled) {
        return {
          isOver: false,
          canDrop: false,
        };
      }

      // `canDrop` is intentionally computed for every drop target while a drag
      // is in progress (not only the hovered one) so that all valid tables show
      // the "available" highlight ring, signalling where the reservation can be
      // dropped. `isOver` stays scoped to the table under the pointer for the
      // stronger hover/border treatment.
      // react-dnd unregisters drop targets mid-dispatch when a drop re-renders
      // the floor plan; monitor reads then throw for the stale target.
      let item: DragReservationItem | null = null;
      let isOver = false;
      let monitorCanDrop = false;
      try {
        item = monitor.getItem() as DragReservationItem | null;
        isOver = monitor.isOver({ shallow: true });
        monitorCanDrop = monitor.canDrop();
      } catch {
        return {
          isOver: false,
          canDrop: false,
        };
      }

      return {
        isOver,
        canDrop:
          monitorCanDrop &&
          canDropReservationOnTimes(item, times, sharedTable),
      };
    },
  });

  return {
    isOver,
    canDrop,
    drop,
  };
}

export function useDrag<IItem = any>(
  item: IItem,
  type: string,
  disabled = false,
) {
  // if (!USE_DROP || disabled)
  //   return {
  //     opacity: 1,
  //     drag: null,
  //   };

  const [{ opacity, isDragging }, drag, preview] = useReactDrag(
    () => ({
      type,
      item: () => {
        markDragGesture();
        return item;
      },
      canDrag: !disabled,
      end: markDragGesture,
      collect: (monitor) => ({
        isDragging: monitor.isDragging(),
        opacity: monitor.isDragging() ? 0.4 : 1,
      }),
    }),
    [item, type, disabled],
  );

  return {
    opacity,
    isDragging,
    drag: disabled ? null : drag,
    preview: disabled ? null : preview,
  };
}

const ReservationDragPreviewLayer = () => {
  const touchDevice = typeof window !== 'undefined' && isTouchDevice();
  const { item, itemType, isDragging, currentOffset } = useReactDragLayer(
    (monitor) => ({
      item: monitor.getItem() as DragReservationItem | null,
      itemType: monitor.getItemType(),
      isDragging: monitor.isDragging(),
      currentOffset: monitor.getClientOffset(),
    }),
  );

  if (
    !touchDevice ||
    !isDragging ||
    itemType !== 'reservation' ||
    !currentOffset
  ) {
    return null;
  }

  const preview = item?.preview;
  const status = preview?.status || 'normal';
  const viewportHeight =
    typeof window !== 'undefined' ? window.innerHeight : null;
  const nearBottom =
    viewportHeight !== null && currentOffset.y > viewportHeight - 170;
  const previewOffsetY = nearBottom ? -88 : 12;

  return (
    <div
      style={{
        position: 'fixed',
        pointerEvents: 'none',
        zIndex: 100000,
        left: 0,
        top: 0,
        width: 260,
        transform: `translate3d(${currentOffset.x + 12}px, ${
          currentOffset.y + previewOffsetY
        }px, 0)`,
      }}
    >
      <div
        className={`reservation-card resa-${status}`}
        style={{
          opacity: 0.96,
          boxShadow: '0 10px 30px rgba(0, 0, 0, 0.24)',
          backgroundColor: preview?.backgroundColor,
        }}
      >
        <div
          className={status === 'normal' ? 'text-3' : 'text-white'}
          style={{ fontWeight: 700, lineHeight: 1.35 }}
        >
          {preview?.title || 'Reservation'}
        </div>
        <div
          className={status === 'normal' ? 'text-subdued' : 'text-white'}
          style={{ marginTop: 2, fontSize: 13, lineHeight: 1.35 }}
        >
          {preview?.subtitle ||
            `${item?.from ?? ''} - ${item?.till ?? ''} | ${
              item?.guests ?? 0
            } PAX`}
        </div>
      </div>
    </div>
  );
};

const DragAndDropProvider: React.FC<{}> = ({ children }) => {
  // if (!USE_DROP) return <>{children}</>;
  const environment = getDragAndDropEnvironment();

  // With enableMouseEvents the same backend also drives mouse gestures, so a
  // mouse drag must not inherit the classification of the last touch.
  React.useEffect(() => {
    document.addEventListener('touchstart', trackGestureSurface, true);
    document.addEventListener('mousedown', trackGestureSurface, true);
    return () => {
      document.removeEventListener('touchstart', trackGestureSurface, true);
      document.removeEventListener('mousedown', trackGestureSurface, true);
    };
  }, []);

  return (
    <DndProvider
      backend={environment.backend === 'touch' ? TouchBackend : HTML5Backend}
      options={
        environment.backend === 'touch'
          ? {
              ...environment.touchOptions,
              enableMouseEvents: environment.mouseEventsEnabled,
              // Read per gesture, not once per render.
              get scrollAngleRanges() {
                return gestureStartedOnDragOnlySurface
                  ? undefined
                  : VERTICAL_SCROLL_ANGLE_RANGES;
              },
            }
          : undefined
      }
    >
      <ReservationDragPreviewLayer />
      {children}
    </DndProvider>
  );
};

export default DragAndDropProvider;
